import React from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { canReturnTo, ownSpaceRedirect } from '@/lib/auth/roles';
import { useApp } from '@/hooks/use-app-context';
import { Button } from '@/components/ui/button';
import { Icon } from '@/lib/icons';
import { SectionGuard } from '@/components/section-guard';
import { saveAuthReturnPath } from '@/lib/auth-return';
import { useAuthPlatformFigures } from '@/hooks/use-platform-stats';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  requirePolicy?: boolean;
  requireSectionValidation?: boolean;
}

export function ProtectedRoute({
  children,
  requireAuth = true,
  requirePolicy = false,
  requireSectionValidation = false,
}: ProtectedRouteProps) {
  const { isAuthenticated, hasAcceptedPolicy, t, user } = useApp();
  const { lang } = useParams();
  const currentLang = lang || 'fr';
  const location = useLocation();

  if (requireAuth && !isAuthenticated) {
    saveAuthReturnPath();
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute left-1/4 top-1/4 h-32 w-32 rounded-full bg-primary/5 blur-2xl" />
          <div className="absolute bottom-1/4 right-1/4 h-32 w-32 rounded-full bg-accent/5 blur-2xl" />
        </div>

        <div className="relative w-full max-w-lg space-y-6">
          <header className="relative py-8">
            <div className="text-center">
              <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full border border-border/40 bg-background/60">
                <Icon name="shield" className="h-8 w-8 text-primary/70" />
              </div>
              <h1 className="font-semibold tracking-tight text-foreground text-3xl sm:text-4xl">
                {t.protectedAccess}
              </h1>
              <p className="mt-4 max-w-xl text-sm leading-7 text-muted-foreground/80 sm:text-base">
                {t.protectedAccessDesc}
              </p>
            </div>
          </header>

          <section className="py-7">
            <div className="space-y-3">
              <InviteItems t={t} />
            </div>
          </section>

          <section className="py-7">
            <a href={`/${currentLang}/login`} className="flex justify-center">
              <Button className="h-11 w-full rounded-full sm:w-auto border border-primary/20 bg-primary px-5 text-sm font-semibold text-primary-foreground shadow-none transition-colors hover:bg-primary/90">
                <Icon name="logIn" className="mr-2 h-4 w-4" />
                {t.login}
                <Icon name="arrowRight" className="ml-2 h-4 w-4" />
              </Button>
            </a>
            <p className="mt-4 text-center text-sm text-muted-foreground/70">
              {t.noAccount}{' '}
              <a href={`/${currentLang}/signup`} className="font-semibold text-primary transition-colors hover:text-primary/80">
                {t.signupLink}
              </a>
            </p>
          </section>

          <div className="text-center">
            <a href={`/${currentLang}`} className="text-sm text-muted-foreground/70 transition-colors hover:text-foreground">
              ← {t.backToHome}
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Contribution pages are not for admins, moderators or accountants.
  if (requireAuth && isAuthenticated && user && !canReturnTo(location.pathname, user.role, currentLang)) {
    return <Navigate to={ownSpaceRedirect(location.pathname, user.role, currentLang)} replace />;
  }

  if (requirePolicy && !hasAcceptedPolicy) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <div className="w-full max-w-lg space-y-6">
          <header className="relative py-8">
            <div className="text-center">
              <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full border border-border/40 bg-background/60">
                <Icon name="fileText" className="h-8 w-8 text-primary/70" />
              </div>
              <h1 className="font-semibold tracking-tight text-foreground text-3xl sm:text-4xl">
                {t.dataPolicyRequired}
              </h1>
              <p className="mt-4 max-w-xl text-sm leading-7 text-muted-foreground/80 sm:text-base">
                {t.dataPolicyRequiredDesc}
              </p>
            </div>
          </header>

          <section className="py-7">
            <div className="rounded-2xl border border-border/40 bg-background/60 p-4">
              <p className="text-[0.64rem] font-semibold uppercase tracking-[0.22em] text-primary/60">{t.whyThisStep}</p>
              <p className="mt-2 text-sm leading-7 text-muted-foreground/80">{t.privacyRespect}</p>
            </div>
          </section>

          <section className="py-7">
            <a href={`/${currentLang}/data-policy?next=${encodeURIComponent(location.pathname + location.search)}`} className="flex justify-center">
              <Button className="h-11 w-full rounded-full sm:w-auto border border-primary/20 bg-primary px-5 text-sm font-semibold text-primary-foreground shadow-none transition-colors hover:bg-primary/90">
                <Icon name="fileText" className="mr-2 h-4 w-4" />
                {t.readDataPolicy}
                <Icon name="arrowRight" className="ml-2 h-4 w-4" />
              </Button>
            </a>
          </section>

          <div className="text-center">
            <a href={`/${currentLang}`} className="text-sm text-muted-foreground/70 transition-colors hover:text-foreground">
              ← {t.backToHome}
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (requireSectionValidation) {
    return <SectionGuard>{children}</SectionGuard>;
  }

  return <>{children}</>;
}

/**
 * The invitation shown to a visitor who is not signed in. The figures are the
 * live ones of the home page; they used to be a hardcoded "+415 contributeurs"
 * and "11 langues". Rendered only for signed-out visitors, so signed-in pages
 * never fetch them.
 */
function InviteItems({ t }: { t: ReturnType<typeof useApp>['t'] }) {
  const { registeredUsers, languages } = useAuthPlatformFigures();
  const items = [
    { icon: 'heart', text: t.contributeAfricanAI },
    {
      icon: 'users',
      text:
        registeredUsers != null && registeredUsers > 0
          ? t.joinContributorsCount.replace('{count}', registeredUsers.toLocaleString())
          : t.joinPlusContributors,
    },
    {
      icon: 'target',
      text: languages
        ? t.preserveLanguagesCount.replace('{count}', languages)
        : t.preserveWestAfricanLanguages,
    },
  ];

  return (
    <>
      {items.map((item) => (
        <div key={item.text} className="flex items-center gap-3 rounded-2xl border border-border/40 bg-background/60 px-4 py-3">
          <Icon name={item.icon as never} className="h-4 w-4 shrink-0 text-primary/50" />
          <span className="text-sm text-foreground/90">{item.text}</span>
        </div>
      ))}
    </>
  );
}
